import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useDocumentTitle, useIsMounted } from 'usehooks-ts';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuthHttp } from '@hooks';
import { CONNECT_OAUTH_ACCOUNT_URL } from '@constants';
import { forgetEbayConsentState, readEbayConsentState } from '@utils';
import {
  confirmationMessageAtom,
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
} from '../../store/jotai';
import { EBAY_PLATFORM } from './integrationsConfig';
import { firstValidationMessage } from './integrationsHelpers';

interface ConnectedEbayAccount {
  platform?: string;
  connectedAccountUsername?: string | null;
}

type EbayConnectionOutcome =
  | { status: 'connecting'; authorisationCode: string }
  | { status: 'cancelled' }
  | { status: 'notConnected'; reason: string };

const SIGN_IN_LINK_NO_LONGER_VALID =
  'This eBay sign-in link is no longer valid — start again from Integrations.';
const CONNECTION_FAILED =
  'Something went wrong while connecting eBay — start again from Integrations.';

function outcomeOfEbayRedirect(
  searchParams: URLSearchParams
): EbayConnectionOutcome {
  const authorisationCode = searchParams.get('code');
  const rememberedState = readEbayConsentState();
  const isReplyFromEbay = searchParams.toString() !== '';
  if (isReplyFromEbay && !authorisationCode) return { status: 'cancelled' };
  if (
    authorisationCode &&
    rememberedState &&
    searchParams.get('state') === rememberedState
  ) {
    return { status: 'connecting', authorisationCode };
  }
  return { status: 'notConnected', reason: SIGN_IN_LINK_NO_LONGER_VALID };
}

function confirmationOfConnecting(account: ConnectedEbayAccount) {
  return account.connectedAccountUsername
    ? `Connected to eBay as ${account.connectedAccountUsername}`
    : 'Connected to eBay';
}

function EbayConnectionPanel({ children }: { children: ReactNode }) {
  return (
    <main className='flex min-h-[50vh] items-center justify-center p-4'>
      <div className='flex w-full max-w-md flex-col items-center gap-4 rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm'>
        {children}
      </div>
    </main>
  );
}

export function CompleteEbayConnection() {
  useDocumentTitle('FLOX - Connect eBay');
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setConfirmationMessage = useSetAtom(confirmationMessageAtom);
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const hasHandledEbayRedirect = useRef(false);
  const isThisPageOpen = useIsMounted();
  const [outcome, setOutcome] = useState<EbayConnectionOutcome>(() =>
    outcomeOfEbayRedirect(searchParams)
  );

  useEffect(() => {
    if (hasHandledEbayRedirect.current) return;
    hasHandledEbayRedirect.current = true;
    forgetEbayConsentState();
    navigate({ search: '' }, { replace: true });
    if (outcome.status !== 'connecting') return;
    const { authorisationCode } = outcome;

    const showWhyEbayIsNotConnected = (reason: string) => {
      if (!isThisPageOpen()) return;
      setErrorResponseStatusCode(null);
      setResponseErrorMessages([]);
      setOutcome({ status: 'notConnected', reason });
    };

    const connectEbayAccount = async () => {
      try {
        const { data } = await authHttp.post<ConnectedEbayAccount>(
          CONNECT_OAUTH_ACCOUNT_URL,
          { Platform: EBAY_PLATFORM, AuthorisationCode: authorisationCode }
        );
        if (data?.platform !== EBAY_PLATFORM) {
          showWhyEbayIsNotConnected(CONNECTION_FAILED);
          return;
        }
        setConfirmationMessage(confirmationOfConnecting(data));
        if (isThisPageOpen()) {
          navigate('/integrations', { replace: true });
        }
      } catch (error) {
        showWhyEbayIsNotConnected(
          firstValidationMessage(error) ?? CONNECTION_FAILED
        );
      }
    };

    connectEbayAccount();
  }, [
    authHttp,
    isThisPageOpen,
    navigate,
    outcome,
    setConfirmationMessage,
    setErrorResponseStatusCode,
    setResponseErrorMessages,
  ]);

  if (outcome.status === 'connecting') {
    return (
      <EbayConnectionPanel>
        <FloxSpinner size='lg' />
        <p className='text-sm text-gray-600'>Connecting your eBay account…</p>
      </EbayConnectionPanel>
    );
  }

  return (
    <EbayConnectionPanel>
      <h1 className='text-lg font-semibold text-gray-900'>
        {outcome.status === 'cancelled'
          ? 'eBay connection cancelled'
          : 'eBay not connected'}
      </h1>
      {outcome.status === 'cancelled' ? (
        <p className='text-sm text-gray-600'>
          Nothing was changed. You can connect eBay from Integrations at any
          time.
        </p>
      ) : (
        <p role='alert' className='text-sm text-gray-600'>
          {outcome.reason}
        </p>
      )}
      <Link
        to='/integrations'
        className='inline-flex items-center justify-center rounded-lg border border-primary px-4 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-blue-50'
      >
        Back to Integrations
      </Link>
    </EbayConnectionPanel>
  );
}
